import React, { useState, useEffect } from "react"
import {
  Upload,
  Button,
  message,
  Progress,
  Alert,
  Modal,
  Space,
  Spin,
} from "antd"
import { InboxOutlined } from "@ant-design/icons"
import SustainabilityCSVPreviewModal from "./SustainabilityCSVPreviewModal"
import csvuploadIcon from "../../../assets/images/export.png"
import csvimportIcon from "../../../assets/images/import-green.png"
import csvimportIconWhite from "../../../assets/images/export-white.png"

const { Dragger } = Upload

const SustainabilityCSVUploadModal = ({
  rubricId,
  visible,
  onClose,
  onUploadSuccess,
}) => {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [showPreview, setShowPreview] = useState(false)
  const [previewData, setPreviewData] = useState({
    domains: [],
    indicators: [],
  })
  const [confirmingUpload, setConfirmingUpload] = useState(false)
  const [domainFileList, setDomainFileList] = useState([])
  const [indicatorFileList, setIndicatorFileList] = useState([])
  const [domainErrors, setDomainErrors] = useState([])
  const [indicatorErrors, setIndicatorErrors] = useState([])
  const [loading, setLoading] = useState(false)
  const [domainLoading, setDomainLoading] = useState(false)
  const [indicatorLoading, setIndicatorLoading] = useState(false)
  const [selectedDomainFile, setSelectedDomainFile] = useState(null)
  const [selectedIndicatorFile, setSelectedIndicatorFile] = useState(null)
  const [uploadedDomain, setUploadedDomain] = useState(false)
  const [uploadedIndicator, setUploadedIndicator] = useState(false)

  useEffect(() => {
    const domainProcessed = selectedDomainFile ? uploadedDomain : true
    const indicatorProcessed = selectedIndicatorFile ? uploadedIndicator : true
    const hasValidationErrors = domainErrors.length > 0 || indicatorErrors.length > 0

    const shouldShow = !hasValidationErrors
      && !loading
      && ((selectedDomainFile && domainProcessed)
        || (selectedIndicatorFile && indicatorProcessed))

    if (shouldShow) {
      setShowPreview(true)
    }
  }, [
    selectedDomainFile,
    selectedIndicatorFile,
    uploadedDomain,
    uploadedIndicator,
    domainErrors,
    indicatorErrors,
    loading,
  ])

  const handleFileChange = (info, type) => {
    const { file } = info
    setDomainErrors([])
    setIndicatorErrors([])

    // Reset uploaded flags when new file is selected
    setUploadedDomain(false)
    setUploadedIndicator(false)

    // Reset loading states when new file is selected
    setLoading(false)
    setDomainLoading(false)
    setIndicatorLoading(false)
    setUploading(false)
    setUploadProgress(0)
    if (file) {
      if (type === "domain_summary") {
        setSelectedDomainFile(file)
        setDomainFileList([file])
        setDomainErrors([])
      }

      if (type === "indicator_details") {
        setSelectedIndicatorFile(file)
        setIndicatorFileList([file])
        setIndicatorErrors([])
      }
    } else {
      // Clear files when removed
      if (type === "domain_summary") {
        setSelectedDomainFile(null)
        setDomainFileList([])
      }

      if (type === "indicator_details") {
        setSelectedIndicatorFile(null)
        setIndicatorFileList([])
      }
    }
    // if (file) {
    //   // Validate file type based on section
    //   if (type === "domain_summary") {
    //     // Set domain file (don't clear indicator file - allow both)
    //     setSelectedDomainFile(file);
    //     setDomainFileList([file]); // Use only the latest file
    //     setDomainErrors([]);
    //   } else if (type === "indicator_details") {
    //     // Set indicator file (don't clear domain file - allow both)
    //     setSelectedIndicatorFile(file);
    //     setIndicatorFileList([file]); // Use only the latest file
    //     setIndicatorErrors([]);
    //   }
    // } else {
    //   // Clear files when removed
    //   if (type === "domain_summary") {
    //     setSelectedDomainFile(null);
    //     setDomainFileList([]);
    //   } else if (type === "indicator_details") {
    //     setSelectedIndicatorFile(null);
    //     setIndicatorFileList([]);
    //   }
    // }
  }
  const validateIndicatorData = (data) => {
    const errors = []
    const processedData = []

    // Score text to number mapping (for frontend validation)
    const SCORE_MAPPING = {
      initiating: 0,
      developing: 1,
      proficient: 2,
      "well developed": 3,
      "well-developed": 3,
    }

    // Indicator title mapping from Ruby file
    const INDICATOR_TITLE_MAPPING = {
      1: "Program Visibility Within the School",
      2: "Program Visibility to members outside of the immediate school community",
      3: "Communication modeling and follow up on program expectations",
      4: "Stakeholder knowledge of program expectations and fluency of program language",
      5: "Systems to monitor program fidelity",
      6: "Quality of program fidelity",
      7: "Quality of data collection",
      8: "Use of data to iterate on program implementation",
      9: "Communication of data with stakeholders",
      10: "Systems and visibility of program leadership",
      11: "Intentionality of SEL Leadership Team communication",
      12: "Systemization of program review",
      13: "Program innovation and fidelity",
      14: "Systematization of communication channels within the school",
      15: "Systematization of communication channels with external supports",
      16: "Systematization of communication of programs strengths and challenges",
      17: "Systematization and quality of feedback to implementing staff",
      18: "Systematization, consistency and quality of program implementation meetings",
      19: "Capacity and consistency of school around SEL Team providing ongoing professional development",
      20: "Quality of communication integration of program to school's mission and vision",
      21: "Distribution of program leadership",
      22: "Quality of SEL Team Leadership communication",
      23: "Intentionality of opportunities to recognize success",
      24: "Resources allocated to program sustainability",
      25: "Quality of program integration (range)",
      26: "Quality of program integration (coordination)",
      27: "Program innovation and fidelity",
    }

    data.forEach((row, index) => {
      const rowNum = index + 2 // CSV rows start from row 2 (headers are row 1)
      const processedRow = { ...row }

      // Check if indicator number is required (not empty/null)
      if (
        !row.indicator_number
        || row.indicator_number === ""
        || row.indicator_number === null
      ) {
        errors.push(`Row ${rowNum}: Indicator Number is required`)
        return // Skip processing this row
      }

      // Check if score is required (not empty/null)
      if (row.score === "" || row.score === null || row.score === undefined) {
        errors.push(`Row ${rowNum}: Score is required`)
        return // Skip processing this row
      }

      // Validate indicator number range
      // const indicatorNum = parseInt(row.indicator_number)
      // if (isNaN(indicatorNum) || indicatorNum < 1 || indicatorNum > 27)
      const indicatorNum = parseInt(row.indicator_number, 10)
      if (Number.isNaN(indicatorNum)) {
        errors.push(
          `Row ${rowNum}: Invalid indicator number ${row.indicator_number}. Valid range is 1-27.`,
        )
        return // Skip processing this row
      }

      // Handle score - accept both text and numeric
      let scoreNum
      if (typeof row.score === "string") {
        // Text score - map to number
        const scoreText = String(row.score).toLowerCase().trim()
        scoreNum = SCORE_MAPPING[scoreText]

        if (scoreNum === undefined) {
          errors.push(
            `Row ${rowNum}: Invalid Score "${row.score}". Valid is initiating, developing, proficient and well developed only.`,
          )
          return // Skip processing this row
        }
      } else {
        // Numeric score - validate range
        scoreNum = parseInt(row.score, 10)
        if (Number.isNaN(scoreNum) || scoreNum < 0 || scoreNum > 3) {
          // scoreNum = parseInt(row.score, 10)
          // if (Number.isNaN(scoreNum) || scoreNum < 0 || scoreNum > 3)
          errors.push(
            `Row ${rowNum}: Invalid Score "${row.score}". Valid is initiating, developing, proficient and well developed only.`,
          )
          return // Skip processing this row
        }
      }

      // Update score to numeric value
      processedRow.score = scoreNum

      // Auto-populate title if missing using INDICATOR_TITLE_MAPPING
      if (!row.title || row.title === "" || row.title === null) {
        const mappedTitle = INDICATOR_TITLE_MAPPING[indicatorNum]

        if (mappedTitle) {
          processedRow.title = mappedTitle
        } else {
          errors.push(
            `Row ${rowNum}: Invalid indicator number ${indicatorNum}. Valid range is 1-27.`,
          )
          return // Skip processing this row
        }
      }

      // Handle justification based on score
      if (scoreNum === 2 || scoreNum === 3) {
        // Proficient or Well Developed - justification required
        if (
          !row.justification
          || row.justification === ""
          || row.justification === null
        ) {
          errors.push(
            `Row ${rowNum}: Justification is required when score is Proficient and well developed`,
          )
          return // Skip processing this row
        }
        processedRow.justification = row.justification
      } else if (scoreNum === 0 || scoreNum === 1) {
        // Initiating or Developing - justification optional
        if (
          !row.justification
          || row.justification === ""
          || row.justification === null
        ) {
          processedRow.justification = "N/A"
        } else {
          processedRow.justification = row.justification
        }
      }

      processedData.push(processedRow)
    })

    return { errors, processedData }
  }
  const uploadFile = (file, formatType) => {
    if (file.size > 10 * 1024 * 1024) {
      message.error("File must be smaller than 10MB!")
      return
    }

    // Set specific loading state based on format type
    if (formatType === "domain_summary") {
      setDomainLoading(true)
    } else if (formatType === "indicator_details") {
      setIndicatorLoading(true)
    }

    setLoading(true)
    setUploading(true)
    setUploadProgress(0)

    const formData = new FormData()
    formData.append("file", file)
    formData.append("format_type", formatType)

    const startTime = Date.now()

    APIClient.post(
      `/sustainability_rubrics/${rubricId}/preview_csv`,
      formData,
      (response) => {
        // Calculate minimum spin time (1 second)
        const elapsed = Date.now() - startTime
        const minimumSpinTime = 1000
        const remainingTime = Math.max(0, minimumSpinTime - elapsed)

        setTimeout(() => {
          // Reset specific loading state based on format type
          if (formatType === "domain_summary") {
            setDomainLoading(false)
          } else if (formatType === "indicator_details") {
            setIndicatorLoading(false)
          }

          setLoading(false)

          // Update preview data based on format type
          if (formatType === "domain_summary") {
            setPreviewData((prev) => {
              const newData = { ...prev, domains: response.data || [] }
              return newData
            })
            setUploadedDomain(true)
          } else if (formatType === "indicator_details") {
            // Apply validation and auto-population to indicator data
            const validation = validateIndicatorData(response.data || [])

            if (validation.errors.length > 0) {
              // Set validation errors and don't proceed to preview
              setIndicatorErrors(validation.errors)
              setIndicatorLoading(false)
              setLoading(false)
              setUploading(false)
              setUploadProgress(0)
              return
            }

            // Use the processed data (with auto-populated fields)
            setPreviewData((prev) => {
              const newData = { ...prev, indicators: validation.processedData }
              return newData
            })
            setUploadedIndicator(true)
          }

          setUploading(false)
          setUploadProgress(0)

          // Don't check for preview here - let the individual file handlers set the uploaded flags
          // The preview will be shown when both files are processed
        }, remainingTime)
      },
      (error) => {
        // Reset specific loading state based on format type
        if (formatType === "domain_summary") {
          setDomainLoading(false)
        } else if (formatType === "indicator_details") {
          setIndicatorLoading(false)
        }

        setLoading(false)
        setUploading(false)
        setUploadProgress(0)

        // Clear only the invalid file when validation errors occur
        if (formatType === "domain_summary") {
          setDomainFileList([])
          setSelectedDomainFile(null)
          setDomainErrors([error.message || "Validation failed"])
        } else if (formatType === "indicator_details") {
          setIndicatorFileList([])
          setSelectedIndicatorFile(null)
          setIndicatorErrors([error.message || "Validation failed"])
        }

        // Handle validation errors
        if (error.errors && Array.isArray(error.errors)) {
          if (formatType === "domain_summary") {
            setDomainErrors(error.errors)
          } else if (formatType === "indicator_details") {
            setIndicatorErrors(error.errors)
          }
        } else {
          // Extract error message from object or use as string
          const errorMessage = error && typeof error === "object" && error.error
            ? error.error
            : error || "Failed to upload file"
          message.error(errorMessage)
          if (formatType === "domain_summary") {
            setDomainErrors([errorMessage])
          } else if (formatType === "indicator_details") {
            setIndicatorErrors([errorMessage])
          }
        }
      },
      {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          )
          setUploadProgress(progress)
        },
      },
    )
  }
  const handleUpload = () => {
    // Check if at least one file is selected
    if (!selectedDomainFile && !selectedIndicatorFile) {
      message.error("Please select at least one file to upload")
      return
    }

    // Upload domain file if selected
    if (selectedDomainFile) {
      uploadFile(selectedDomainFile, "domain_summary")
    }

    // Upload indicator file if selected
    if (selectedIndicatorFile) {
      uploadFile(selectedIndicatorFile, "indicator_details")
    }
  }
  const handleClose = () => {
    setShowPreview(false)
    setPreviewData({ domains: [], indicators: [] })

    // Reset loading states when modal is closed
    setLoading(false)
    setDomainLoading(false)
    setIndicatorLoading(false)
    setUploading(false)
    setUploadProgress(0)

    // Reset uploaded flags to allow re-uploading
    setUploadedDomain(false)
    setUploadedIndicator(false)

    // Clear files and errors
    setDomainFileList([])
    setIndicatorFileList([])
    setDomainErrors([])
    setIndicatorErrors([])
    setSelectedDomainFile(null)
    setSelectedIndicatorFile(null)

    // Call parent onClose to actually close the modal
    onClose()
  }
  const handleConfirmUpload = () => {
    setConfirmingUpload(true)

    // Upload both files if both were uploaded
    const uploadPromises = []

    if (selectedDomainFile && uploadedDomain) {
      const domainFormData = new FormData()
      domainFormData.append("file", selectedDomainFile)
      domainFormData.append("format_type", "domain_summary")

      uploadPromises.push(
        new Promise((resolve, reject) => {
          APIClient.post(
            `/sustainability_rubrics/${rubricId}/upload_csv`,
            domainFormData,
            resolve,
            reject,
          )
        }),
      )
    }

    if (selectedIndicatorFile && uploadedIndicator) {
      const indicatorFormData = new FormData()
      indicatorFormData.append("file", selectedIndicatorFile)
      indicatorFormData.append("format_type", "indicator_details")

      uploadPromises.push(
        new Promise((resolve, reject) => {
          APIClient.post(
            `/sustainability_rubrics/${rubricId}/upload_csv`,
            indicatorFormData,
            resolve,
            reject,
          )
        }),
      )
    }

    if (uploadPromises.length === 0) {
      message.error("No files to upload. Please upload files again.")
      setConfirmingUpload(false)
      return
    }

    // Wait for all uploads to complete
    Promise.all(uploadPromises)
      // .then((responses) => {
      .then(() => {
        if (onUploadSuccess) {
          onUploadSuccess()
        }
        handleClose()
        setConfirmingUpload(false)
      })
      .catch(() => {
        message.error("Upload failed. Please try again.")
        setConfirmingUpload(false)
      })
  }

  const downloadTemplate = (type) => {
    // Generate Excel template with dropdown functionality
    const templateUrl = `/api/v1/sustainability_rubrics/${rubricId}/generate_template?type=${type}`

    // Create a temporary link to download the file
    const link = document.createElement("a")
    link.href = templateUrl
    link.download = `sustainability_rubric_${type}_template.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      {/* Only show upload modal when preview is not visible */}
      <Modal
        title={(
          <div className="modal-title">
            <div>
              <span
                style={{
                  display: "block",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                Upload File
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: "normal",
                  color: "#666",
                  marginTop: "4px",
                }}
              >
                Review the parsed indicator data below. Upload a CSV or Excel file (.csv, .xls, .xlsx) or use
                the sample format to get started.
              </span>
            </div>
          </div>
        )}
        open={visible && !showPreview}
        onCancel={handleClose}
        footer={null}
        width={820}
        height={560}
        destroyOnClose
        className="csv-upload-modal"
      >
        <Space
          className="man-space"
          direction="vertical"
          size="large"
          style={{ width: "100%" }}
        >
          <div className="upload-section">
            <div className="section-header">
              <span className="section-title-sustainability">
                Upload Domain Summary
              </span>
              <Button
                type="link"
                icon={(
                  <img
                    src={csvimportIcon}
                    className="csv-upload-modal__file-icon"
                    style={{
                      width: "16px",
                      height: "16px",
                      marginRight: "8px",
                    }}
                  />
                )}
                onClick={() => downloadTemplate("domain_summary")}
                className="download-link"
              >
                <span style={{ color: "#0CA4A5" }}>Download Sample Excel</span>
              </Button>
            </div>
            <Dragger
              name="file"
              multiple={false}
              accept=".csv,.xls,.xlsx"
              fileList={domainFileList}
              onChange={(info) => handleFileChange(info, "domain_summary")}
              beforeUpload={() => false}
              showUploadList={false}
              disabled={loading}
              className="csv-upload-modal__dragger"
            >
              {domainFileList.length === 0 && !domainLoading ? (
                <>
                  <p className="ant-upload-drag-icon">
                    <img
                      src={csvuploadIcon}
                      className="csv-upload-modal__file-icon"
                      style={{ width: "24px", height: "24px" }}
                    />
                  </p>
                  <p className="ant-upload-text">Upload Domain Summary</p>
                  <p className="ant-upload-hint">CSV or Excel (.csv, .xls, .xlsx — Max 10MB)</p>
                </>
              ) : domainLoading ? (
                <div style={{ padding: "40px" }}>
                  <Spin size="large" tip="Validating file..." />
                </div>
              ) : (
                <div className="csv-upload-modal__selected-file-display">
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ fontSize: 48, color: "#1890ff" }} />
                  </p>
                  <p className="ant-upload-text" style={{ color: "#1890ff" }}>
                    {domainFileList[0].name}
                  </p>
                  <p className="ant-upload-hint">Click to change file</p>
                </div>
              )}
            </Dragger>

            {/* Error Messages Display for Domain Summary */}
            {domainErrors.length > 0 && !domainLoading && (
              <div style={{ marginTop: "16px" }}>
                <Alert
                  message="Validation Errors"
                  description={(
                    <div>
                      {domainErrors.map((error, index) => (
                        <div key={index} style={{ marginBottom: "4px" }}>
                          • {error}
                        </div>
                      ))}
                    </div>
                  )}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setDomainErrors([])}
                />
              </div>
            )}
          </div>

          <div className="upload-section">
            <div className="section-header">
              <h4 className="section-title-sustainability">
                Upload Indicator Details
              </h4>
              <Button
                type="link"
                icon={(
                  <img
                    src={csvimportIcon}
                    className="csv-upload-modal__file-icon"
                    style={{
                      width: "16px",
                      height: "16px",
                      marginRight: "8px",
                    }}
                  />
                )}
                onClick={() => downloadTemplate("indicator_details")}
                className="download-link"
              >
                <span style={{ color: "#0CA4A5" }}>Download Sample Excel</span>
              </Button>
            </div>
            <Dragger
              name="file"
              multiple={false}
              accept=".csv,.xls,.xlsx"
              fileList={indicatorFileList}
              onChange={(info) => handleFileChange(info, "indicator_details")}
              beforeUpload={() => false}
              showUploadList={false}
              disabled={loading}
              className="csv-upload-modal__dragger"
            >
              {indicatorFileList.length === 0 && !indicatorLoading ? (
                <>
                  <p className="ant-upload-drag-icon">
                    <img
                      src={csvuploadIcon}
                      className="csv-upload-modal__file-icon"
                      style={{ width: "24px", height: "24px" }}
                    />
                  </p>
                  <p className="ant-upload-text">Upload Indicator Summary</p>
                  <p className="ant-upload-hint">CSV or Excel (.csv, .xls, .xlsx — Max 10MB)</p>
                </>
              ) : indicatorLoading ? (
                <div style={{ padding: "40px" }}>
                  <Spin size="large" tip="Validating file..." />
                </div>
              ) : (
                <div className="csv-upload-modal__selected-file-display">
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ fontSize: 48, color: "#1890ff" }} />
                  </p>
                  <p className="ant-upload-text" style={{ color: "#1890ff" }}>
                    {indicatorFileList[0].name}
                  </p>
                  <p className="ant-upload-hint">Click to change file</p>
                </div>
              )}
            </Dragger>

            {/* Error Messages Display for Indicator Details */}
            {indicatorErrors.length > 0 && !indicatorLoading && (
              <div style={{ marginTop: "16px" }}>
                <Alert
                  message="Validation Errors"
                  description={(
                    <div>
                      {indicatorErrors.map((error, index) => (
                        <div key={index} style={{ marginBottom: "4px" }}>
                          • {error}
                        </div>
                      ))}
                    </div>
                  )}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setIndicatorErrors([])}
                />
              </div>
            )}
          </div>

          {uploading && uploadProgress > 0 && (
            <Progress
              percent={uploadProgress}
              status="active"
              strokeColor={{
                "0%": "#108ee9",
                "100%": "#87d068",
              }}
            />
          )}

          <div className="upload-button-container">
            <Button
              icon={(
                <img
                  src={csvimportIconWhite}
                  className="csv-upload-modal__file-icon"
                  style={{ width: "16px", height: "16px", marginRight: "8px" }}
                />
              )}
              type="primary"
              onClick={handleUpload}
              loading={loading}
              disabled={
                loading
                || (selectedDomainFile === null
                  && selectedIndicatorFile === null)
                || domainErrors.length > 0
                || indicatorErrors.length > 0
              }
              className="upload-button"
            >
              <span style={{ color: "white" }}>Upload File</span>
            </Button>
          </div>
        </Space>
      </Modal>

      <SustainabilityCSVPreviewModal
        visible={showPreview}
        onClose={handleClose}
        domainData={previewData.domains}
        indicatorData={previewData.indicators}
        onConfirmUpload={handleConfirmUpload}
        loading={confirmingUpload}
      />
    </>
  )
}
export default SustainabilityCSVUploadModal
